<template>
  <div :class="additionalClasses">
    <section class="section" v-if="hasInsufficientPoints">
      <div class="container">
        <div class="row">
          <div
            class="col-12 error-box mb-3"
            v-text="translate('lblInsufficientPoints')"
          ></div>
        </div>
      </div>
    </section>
    <section class="section">
      <div class="container">
        <div class="row">
          <form class="award-checkout-form no-validate" @submit.prevent="submitForm" ref="form">
            <h2 class="h2" v-text="translate('lblCheckoutFormHeadline')"></h2>
            <div class="container">
              <div class="form-component__container">
                <div class="form-component">
                  <h3 class="h3 bold" v-text="translate('lblContactDataHeadline')"></h3>
                  <div class="row" v-for="(row, i) in contactFields" :key="i">
                    <div class="col-12 col-m-6" v-for="(field, j) in row" :key="j">
                      <component
                        :id="`${i}-${j}-${field.name}`"
                        :name="field.name"
                        :label="field.label"
                        :required="field.required"
                        :pattern="field.pattern"
                        :options="field.options"
                        :value="field.value"
                        :show-errors="showErrors"
                        :invalid="field.invalid"
                        :disabled="true"
                        :is="field.type"
                        @change="updateContactData"
                        class="mt-3"
                      ></component>
                    </div>
                  </div>
                </div>
                <div class="form-component" v-if="shippingFields">
                  <h3 class="h3 bold" v-text="translate('lblShippingAddressHeadline')"></h3>
                  <div v-html="translate('lblShippingAddressDescription')"></div>
                  <div class="buttons">
                    <button
                      type="button" class="button button--medium button--primary"
                      v-text="translate('btnApplyContactInformation')"
                      @click="applyContactInformation"
                    ></button>
                  </div>
                  <div class="row" v-for="(row, i) in shippingFields" :key="i">
                    <div class="col-12 col-m-6" v-for="(field, j) in row" :key="j">
                      <component
                        :name="field.name"
                        :label="field.label"
                        :required="field.required"
                        :pattern="field.pattern"
                        :options="field.options"
                        :value="field.value"
                        :show-errors="showErrors"
                        :invalid="field.invalid"
                        :is="field.type"
                        @change="updateShippingData"
                        class="mt-3"
                      ></component>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="container">
              <div class="row">
                <div class="button-row col-12 d-flex flex-s-column flex-m-row justify-content-between px-0 mb-5">
                  <button type="button"
                          class="flex-last flex-m-first mb-3 mb-m-0 button button--medium button--cancel"
                          v-text="translate('btnCancel')"
                          @click="cancel"
                  ></button>
                  <button type="submit"
                          :class="['flex-first flex-m-last mb-3 mb-m-0 button button--medium button--primary', showErrors && hasErrors ? 'disabled grey' : null]"
                          v-text="translate('btnSubmit')"
                          :disabled="hasInsufficientPoints"
                          @click="checkValidityState(true)"
                  ></button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import TextInput from './AwardCheckoutFormInputText.vue';
import Dropdown from './AwardCheckoutFormDropdown.vue';
import { getValueForPropertyPath, hasOwnDeepProperty } from '../../shared/Utils';

export default {
  name: 'AwardCheckoutForm',
  data: () => ({
    shippingFields: null,
    showErrors: false,
    hasErrors: true,
  }),
  props: {
    contactFields: Array,
    additionalClasses: String,
    hasInsufficientPoints: Boolean,
  },
  methods: {
    update(toBeUpdated, data) {
      const found = toBeUpdated.flat().find((field) => field.name === data.name);
      if (found) {
        found.value = data.value;
        found.invalid = data.invalid;
        if (found.name === 'country') {
          this.updateCountry(toBeUpdated, data);
        }
      }
      this.$forceUpdate();
      this.checkValidityState();
    },
    updateContactData(data) {
      this.update(this.contactFields, data);
    },
    updateShippingData(data) {
      this.update(this.shippingFields, data);
    },
    updateCountry(toBeUpdated, data) {
      const zipCodeField = toBeUpdated.flat().find((field) => field.name === 'zipcode');
      const countryField = toBeUpdated.flat().find((field) => field.name === data.name);
      if (zipCodeField) {
        zipCodeField.pattern = countryField.options.find((option) => option.value === data.value)?.zipCodePattern;
      }
    },
    applyContactInformation() {
      const contactFields = this.convertToKeyValuePairs(this.contactFields);
      this.shippingFields.flat().forEach((field) => {
        // eslint-disable-next-line no-param-reassign
        field.value = contactFields[field.name];
      });
      this.checkValidityState();
      this.$forceUpdate();
    },
    cancel() {
      window.history.back();
    },
    convertToKeyValuePairs(toBeConverted) {
      return toBeConverted.flat().reduce(
        (obj, field) => {
          // eslint-disable-next-line no-param-reassign
          obj[field.name] = field.value;
          return obj;
        },
        {},
      );
    },
    submitForm() {
      if (!this.checkValidityState()) {
        return;
      }
      this.$emit('submit', {
        contactData: this.convertToKeyValuePairs(this.contactFields),
        shippingData: this.convertToKeyValuePairs(this.shippingFields),
      });
    },
    checkValidityState(showErrors) {
      if (showErrors) {
        this.showErrors = true;
      }
      this.hasErrors = false;
      this.hasErrors = this.hasErrors || this.contactFields.flat().some((field) => field.invalid);
      this.hasErrors = this.hasErrors || this.shippingFields.flat().some((field) => field.invalid);
      return !this.hasErrors;
    },
    addMandatoryIndicator(fields) {
      fields.forEach((field) => {
        if (field.required && !field.label.endsWith('*')) {
          // eslint-disable-next-line no-param-reassign
          field.label = `${field.label} *`;
        }
      });
    },
    fillInUserData() {
      this.contactFields.flat().forEach((field) => {
        if (field.userDataMapping) {
          const propertyPath = field.userDataMapping;
          if (window.KWS?.user && hasOwnDeepProperty(window.KWS.user, propertyPath)) {
            // eslint-disable-next-line no-param-reassign
            field.value = getValueForPropertyPath(window.KWS.user, propertyPath);
          }
        }
      });
    },
  },
  async created() {
    this.shippingFields = JSON.parse(JSON.stringify(this.contactFields));
    this.addMandatoryIndicator(this.shippingFields.flat());
    this.fillInUserData();
  },
  components: {
    TextInput,
    Dropdown,
  },
};
</script>

<style lang="scss">
.award-checkout-form {
  .form-component__container {
    .form-component {
      background-color: $color-kws-light-gray-50;
    }

    .form-component:not(:first-child):before {
      content: ' ';
      width: 100%;
      border: rem(1px) solid $color-kws-light-gray-25;
      position: relative;
      top: rem(-20px);
    }
  }

  [disabled],
  .disabled {
    cursor: not-allowed;
  }
}
</style>
